if (!customElements.get('dd-pdp-product-form')) {
  customElements.define(
    'dd-pdp-product-form',
    class DDPDPProductForm extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ddBound === 'true') return;
        this.dataset.ddBound = 'true';

        this.rows = Array.from(this.querySelectorAll('[data-dd-pdp-purchase-row]'));
        this.form = this.querySelector('form.dd-pdp-form__form');
        this.submitButton = this.querySelector('[data-dd-pdp-submit]');
        this.submitButtons = Array.from(
          this.querySelectorAll('[data-dd-pdp-submit], [data-dd-pdp-sticky-submit]')
        );
        this.primaryAtc = this.querySelector('[data-dd-pdp-primary-atc]');
        this.stickyAtc = this.querySelector('[data-dd-pdp-sticky-atc]');
        this.stickySubmitButton = this.querySelector('[data-dd-pdp-sticky-submit]');
        this.errorOutput = this.querySelector('[data-dd-pdp-error]');
        this.statusOutput = this.querySelector('[data-dd-pdp-status]');
        this.countOutput = this.querySelector('[data-dd-pdp-selected-count]');
        this.pouchCountOutput = this.querySelector('[data-dd-pdp-pouch-count]');
        this.subtotalOutputs = Array.from(this.querySelectorAll('[data-dd-pdp-cta-subtotal]'));
        this.originalSubtotalOutputs = Array.from(this.querySelectorAll('[data-dd-pdp-cta-original-subtotal]'));
        this.variantPicker = this.querySelector('[data-dd-pdp-variant-picker]');
        this.acceleratedCheckout = this.querySelector('[data-dd-pdp-accelerated-checkout]');
        this.recipientForm = this.querySelector('recipient-form');
        this.variantInput = this.querySelector('[data-dd-pdp-current-variant-id]');
        this.currentPriceOutput = this.querySelector('[data-dd-pdp-current-price]');
        this.comparePriceOutput = this.querySelector('[data-dd-pdp-compare-price]');
        this.regularPriceLabel = this.querySelector('[data-dd-pdp-regular-price-label]');
        this.salePriceLabel = this.querySelector('[data-dd-pdp-sale-price-label]');
        this.availableAtcContents = Array.from(this.querySelectorAll('[data-dd-pdp-atc-available]'));
        this.soldOutAtcContents = Array.from(this.querySelectorAll('[data-dd-pdp-atc-sold-out]'));
        this.loadingAtcContents = Array.from(this.querySelectorAll('[data-dd-pdp-atc-loading]'));
        this.addedAtcContents = Array.from(this.querySelectorAll('[data-dd-pdp-atc-added]'));
        this.variants = this.readVariants();
        this.isSubmitting = false;
        this.isCartPending = false;
        this.isAdded = false;
        this.qualificationNotice = this.querySelector('[data-dd-pdp-qualification]');
        this.qualificationMessage = this.querySelector('[data-dd-pdp-qualification-message]');
        this.qualificationHeading = this.querySelector('[data-dd-pdp-qualification-heading]');
        this.cartNote = this.querySelector('[data-dd-pdp-cart-note]');
        try {
          this.commerceCart = JSON.parse(this.querySelector('[data-dd-pdp-commerce-cart]')?.textContent || '{}');
          this.eligibleProductIds = new Set(JSON.parse(this.querySelector('[data-dd-pdp-eligible-products]')?.textContent || '[]').map(String));
        } catch (_error) {
          this.commerceCart = {};
          this.eligibleProductIds = new Set();
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCommerceCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.refreshCommerceCart(event));
        }
        this.handleCommerceCartResume = () => {
          if (document.visibilityState === 'visible' && !this.isSubmitting && !this.isCartPending) this.refreshCommerceCart();
        };
        this.handleCommerceCartStorage = (event) => {
          if (event.key === 'dd:cart-updated') this.handleCommerceCartResume();
        };
        window.addEventListener('focus', this.handleCommerceCartResume);
        window.addEventListener('pageshow', this.handleCommerceCartResume);
        window.addEventListener('storage', this.handleCommerceCartStorage);
        document.addEventListener('visibilitychange', this.handleCommerceCartResume);

        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('change', this.handleChange.bind(this));
        this.form?.addEventListener('submit', this.handleSubmitRequest.bind(this));
        this.rows.forEach((row) => {
          const selector = row.querySelector('[data-dd-pdp-quantity]');
          if (!selector) return;

          const quantity = this.readNumber(selector.dataset.quantity);
          const maximum = selector.dataset.max === undefined ? null : this.readNumber(selector.dataset.max);
          this.setQuantity(selector, quantity, maximum);
        });
        this.hydrateCurrentVariantInput();
        this.setupStickyAtc();
        this.updateVariantOptionAvailability();
        this.updateSummary();
      }

      disconnectedCallback() {
        window.removeEventListener('focus', this.handleCommerceCartResume);
        window.removeEventListener('pageshow', this.handleCommerceCartResume);
        window.removeEventListener('storage', this.handleCommerceCartStorage);
        document.removeEventListener('visibilitychange', this.handleCommerceCartResume);
        window.removeEventListener('pageshow', this.handleRecoveryPageShow);
        this.stickyAtcObserver?.disconnect();
        window.removeEventListener('scroll', this.handleStickyPositionChange);
        this.mobileViewport?.removeEventListener?.('change', this.handleViewportChange);
        this.mobileMenuDetails?.removeEventListener('toggle', this.handleMobileMenuToggle);
        this.mobileMenuDetails?.removeEventListener('keyup', this.handleMobileMenuKeyUp, true);
        this.mobileMenuSummary?.removeEventListener('pointerdown', this.handleMobileMenuPress, true);
        this.mobileMenuSummary?.removeEventListener('click', this.handleMobileMenuClick, true);
        document.removeEventListener('shopify:block:select', this.handleMobileMenuBlockSelect, true);
        window.cancelAnimationFrame(this.stickyPositionFrame);
        window.cancelAnimationFrame(this.mobileMenuRestoreFrame);
        this.cancelMobileMenuScrollRestore?.();
        this.cancelMobileMenuRestoreGrace?.();
        this.stickyAtcMenuScrollY = undefined;
        this.mobileMenuClosePending = false;
        this.mobileMenuReopenRequested = false;
        this.mobileMenuRestoreTargetY = undefined;
        this.mobileMenuRestoreTargetExpiresAt = undefined;
        window.clearTimeout(this.addedStateTimer);
        window.clearTimeout(this.qualificationTimer);
        window.clearTimeout(this.qualificationHideTimer);
        window.cancelAnimationFrame(this.qualificationFrame);
        this.stopQualificationPositioning();
        this.unsubscribeCommerceCart?.();
      }

      handleChange(event) {
        if (!event.target.matches('[data-dd-pdp-option-value]')) return;
        if (this.isSubmitting || this.isCartPending) return;

        this.setAdded(false);
        this.updateVariantOptionAvailability();
        this.applyVariant(this.findSelectedVariant());
      }

      handleClick(event) {
        if (event.target.closest('[data-dd-pdp-qualification]')) {
          event.preventDefault();
          event.stopPropagation();
          this.hideQualification();
          return;
        }
        const stickySubmit = event.target.closest('[data-dd-pdp-sticky-submit]');
        if (stickySubmit) {
          event.preventDefault();
          event.stopPropagation();
          if (!stickySubmit.disabled && !this.isSubmitting) {
            this.submitTrigger = stickySubmit;
            try {
              this.form?.requestSubmit(this.submitButton);
            } finally {
              // Native validation may prevent submit; never retain a stale sticky origin.
              this.submitTrigger = null;
            }
          }
          return;
        }

        const decrement = event.target.closest('[data-dd-pdp-quantity-minus]');
        const increment = event.target.closest('[data-dd-pdp-quantity-plus]');
        if (!decrement && !increment) return;

        event.preventDefault();
        event.stopPropagation();

        const selector = event.target.closest('[data-dd-pdp-quantity]');
        const row = selector?.closest('[data-dd-pdp-purchase-row]');
        if (this.isSubmitting || this.isCartPending || !selector || !row || row.dataset.available !== 'true') return;

        const quantity = this.readNumber(selector.dataset.quantity);
        const minimum = Math.max(1, this.readNumber(selector.dataset.min, 1));
        const step = Math.max(1, this.readNumber(selector.dataset.step, 1));
        const maximum = selector.dataset.max === undefined ? null : this.readNumber(selector.dataset.max);
        const allowZero = selector.dataset.allowZero === 'true';
        let nextQuantity = quantity;

        if (increment) {
          nextQuantity = quantity === 0 ? minimum : quantity + step;
          if (maximum !== null) nextQuantity = Math.min(nextQuantity, maximum);
        } else {
          nextQuantity = quantity <= minimum && allowZero ? 0 : Math.max(minimum, quantity - step);
        }

        this.setAdded(false);
        this.setQuantity(selector, nextQuantity, maximum);
        this.updateSummary(true);
        if (nextQuantity !== quantity) {
          this.cueSelectionCTA();
          window.DDTracking?.emit('dd_quantity_selected', {
            ...window.DDTracking.context(this), productId: String(row.dataset.productId || ''),
            variantId: String(row.dataset.variantId || ''), rowType: row.dataset.rowType,
            quantityBefore: quantity, quantityAfter: nextQuantity, quantityDelta: nextQuantity - quantity,
            direction: nextQuantity > quantity ? 'increase' : 'decrease',
          });
        }
      }

      cueSelectionCTA() {
        if (this.hasSelectionCued) return;
        this.hasSelectionCued = true;
        this.submitButtons.forEach((button) => {
          if (button.closest('[data-dd-pdp-sticky-atc]')?.hidden) return;
          button.classList.add('is-selection-cued');
          button.addEventListener('animationend', () => button.classList.remove('is-selection-cued'), { once: true });
        });
      }

      setQuantity(selector, quantity, maximum) {
        selector.dataset.quantity = String(quantity);
        selector.dataset.quantityState = quantity > 0 ? 'active' : 'zero';

        const output = selector.querySelector('[data-dd-pdp-row-quantity]');
        const input = selector.querySelector('[data-dd-pdp-line-quantity]');
        const decrement = selector.querySelector('[data-dd-pdp-quantity-minus]');
        const increment = selector.querySelector('[data-dd-pdp-quantity-plus]');
        const minimum = Math.max(1, this.readNumber(selector.dataset.min, 1));
        const allowZero = selector.dataset.allowZero === 'true';
        const available = selector.closest('[data-dd-pdp-purchase-row]')?.dataset.available === 'true';
        if (output) output.textContent = String(quantity);
        if (input) input.value = String(quantity);
        if (decrement) decrement.disabled = !available || (!allowZero && quantity <= minimum);
        if (increment) increment.disabled = !available || (maximum !== null && quantity >= maximum);
      }

      updateSummary(notify = false) {
        const lines = this.getLines();
        const selectedLines = lines.filter((line) => line.available && line.variantId && line.quantity > 0);

        const selectedBottleCount = selectedLines.reduce((total, line) => total + line.quantity, 0);
        const subtotal = selectedLines.reduce((total, line) => total + line.quantity * line.unitPrice, 0);
        const hasAdditionalSelection = selectedLines.some((line) => line.rowType !== 'current');
        const selectionPreview = this.previewSelection(selectedLines);

        this.dataset.selectedBottleCount = String(selectedBottleCount);
        const stickyQuantity = this.querySelector('[data-dd-pdp-sticky-quantity]');
        if (stickyQuantity) stickyQuantity.textContent = String(selectedBottleCount);
        if (this.countOutput) this.countOutput.textContent = this.selectionCountText(selectedLines, selectedBottleCount);
        if (this.pouchCountOutput) this.pouchCountOutput.textContent = String(selectedBottleCount * 6);
        this.subtotalOutputs.forEach((output) => {
          output.textContent = this.formatMoney(selectionPreview.subtotal);
        });
        this.originalSubtotalOutputs.forEach((output) => {
          output.hidden = !(selectionPreview.mixQualified && selectionPreview.subtotal < subtotal);
          output.textContent = this.formatMoney(subtotal);
        });
        if (this.cartNote) this.cartNote.hidden = selectionPreview.canPreview;
        this.updateShippingLine(selectionPreview);
        this.updateQualification(selectionPreview, notify);
        if (this.acceleratedCheckout) this.acceleratedCheckout.hidden = hasAdditionalSelection;

        const submitDisabled =
          this.isSubmitting ||
          this.isCartPending ||
          this.isAdded ||
          this.dataset.currentProductAvailable !== 'true' ||
          selectedBottleCount === 0;
        this.submitButtons.forEach((button) => {
          button.disabled = submitDisabled;
        });

        this.dispatchEvent(
          new CustomEvent('dd:pdp-selection-change', {
            bubbles: true,
            detail: { lines, selectedBottleCount, subtotal },
          })
        );
      }

      updateShippingLine(preview) {
        const copies = this.querySelectorAll('[data-dd-pdp-shipping-copy]');
        if (!copies.length) return;
        const cart = this.commerceCart || {};
        const threshold = this.readNumber(this.dataset.shippingThreshold);
        const cartQualified = Array.isArray(cart.items) && this.dataset.shippingCurrency === 'USD' &&
          threshold > 0 && this.readNumber(cart.total_price) >= threshold;
        copies.forEach((copy) => {
          if (copy.closest('[data-dd-pdp-sticky-atc]')) {
            copy.closest('[data-dd-pdp-shipping]').hidden = !(cartQualified || preview.shippingQualified);
          }
          copy.textContent = cartQualified ? copy.dataset.cartQualified :
            preview.shippingQualified ? copy.dataset.selectionQualified : copy.dataset.belowThreshold;
        });
      }

      selectionCountText(lines, count) {
        const units = new Set(lines.map((line) => line.selectionUnit || 'item'));
        const sameUnit = units.size === 1;
        const unit = sameUnit
          ? (count === 1 ? lines[0]?.selectionUnitLabel : lines[0]?.selectionUnitPlural)
          : (count === 1 ? this.dataset.selectionItemSingular : this.dataset.selectionItemPlural);
        return (this.dataset.selectionCountFormat || '__count__ × __unit__ selected')
          .replace('__count__', String(count))
          .replace('__unit__', unit || (count === 1 ? this.dataset.selectionItemSingular : this.dataset.selectionItemPlural) || '');
      }

      previewSelection(lines) {
        const cart = this.commerceCart || {};
        const items = Array.isArray(cart.items) ? cart.items : [];
        const threshold = Math.max(1, this.readNumber(this.dataset.mixMatchThreshold, 3));
        const percent = this.readNumber(this.dataset.mixMatchPercent, 10);
        const isEligible = (item) => this.eligibleProductIds?.has(String(item.product_id)) && !item.selling_plan_allocation;
        const eligibleItems = items.filter(isEligible);
        const cartCount = eligibleItems.reduce((total, item) => total + item.quantity, 0);
        const selectedCount = lines.reduce((total, line) => total + (line.mixMatchEligible ? line.quantity : 0), 0);
        const normalizeTitle = (title = '') => title.toLowerCase().replace(/[^a-z0-9]/g, '');
        const isStashDiscount = (discount) => discount && discount.type !== 'discount_code' && discount.value_type === 'percentage' && this.readNumber(discount.value) === percent && normalizeTitle(discount.title) === normalizeTitle(this.dataset.mixMatchTitle);
        const hasCartDiscounts = (cart.cart_level_discount_applications || []).length > 0 ||
          (cart.discount_codes || []).some((code) => code.applicable);
        const hasOtherLineDiscount = (item) => (item.line_level_discount_allocations || []).some((allocation) => !isStashDiscount(allocation.discount_application));
        // Discounts on unrelated products do not prevent eligible bottles from qualifying.
        const hasMixMatchConflict = hasCartDiscounts || eligibleItems.some(hasOtherLineDiscount);
        const canPreview = Array.isArray(cart.items) && this.dataset.mixMatchEnabled === 'true' && this.eligibleProductIds?.size > 0 && !hasMixMatchConflict;
        const mixQualified = canPreview && cartCount + selectedCount >= threshold;
        const savingPerUnit = (price) => Math.floor((this.readNumber(price) * percent) / 100);
        const subtotal = lines.reduce((total, line) => total + line.quantity *
          (line.unitPrice - (mixQualified && line.mixMatchEligible ? savingPerUnit(line.unitPrice) : 0)), 0);
        const cartSaving = mixQualified && cartCount < threshold
          ? eligibleItems.reduce((total, item) => total + item.quantity * savingPerUnit(item.original_price), 0)
          : 0;
        const projectedTotal = this.readNumber(cart.total_price) - cartSaving + subtotal;
        const shippingThreshold = this.readNumber(this.dataset.shippingThreshold);
        const shippingQualified = Array.isArray(cart.items) && !hasMixMatchConflict && this.dataset.shippingCurrency === 'USD' && shippingThreshold > 0 && projectedTotal >= shippingThreshold;
        const selectedItemCount = selectedCount;
        const basketItemCount = cartCount;
        return { subtotal, projectedTotal, mixQualified, shippingQualified, canPreview, selectedItemCount, basketItemCount };
      }

      updateQualification(preview, notify) {
        const previous = this.selectionQualification;
        this.selectionQualification = preview;
        this.dataset.selectedDiscountedSubtotal = String(preview.subtotal);
        this.dataset.projectedCartTotal = String(preview.projectedTotal);
        const newSaving = previous && !previous.mixQualified && preview.mixQualified;
        const newShipping = previous && !previous.shippingQualified && preview.shippingQualified;
        if (previous && ((previous.mixQualified && !preview.mixQualified) || (previous.shippingQualified && !preview.shippingQualified))) this.hideQualification();
        if (!notify || !previous || this.isSubmitting || this.isCartPending || (!newSaving && !newShipping)) return;
        const template = newSaving && newShipping ? this.dataset.ddSelectionBoth
          : newShipping ? this.dataset.ddSelectionShipping : this.dataset.ddSelectionSaving;
        if (!template || !this.qualificationNotice || !this.qualificationMessage) return;
        window.clearTimeout(this.qualificationTimer);
        window.clearTimeout(this.qualificationHideTimer);
        window.cancelAnimationFrame(this.qualificationFrame);
        const hasBasketItems = preview.basketItemCount > 0;
        const basketTemplate = newSaving && newShipping ? this.dataset.ddSelectionBothBasket
          : newSaving ? this.dataset.ddSelectionSavingBasket : '';
        this.qualificationMessage.textContent = (hasBasketItems && basketTemplate ? basketTemplate : template)
          .replace('__percent__', this.dataset.mixMatchPercent)
          .replace('__selected__', String(preview.selectedItemCount))
          .replace('__cart__', String(preview.basketItemCount))
          .replace('__basket__', hasBasketItems ? this.dataset.ddSelectionBasket || '' : '');
        const headingTemplate = newSaving && newShipping ? this.dataset.ddSelectionBothHeading
          : newShipping ? this.dataset.ddSelectionShippingHeading : this.dataset.ddSelectionSavingHeading;
        const heading = (headingTemplate || '').replace('__percent__', this.dataset.mixMatchPercent);
        if (this.qualificationHeading) {
          this.qualificationHeading.textContent = heading;
          const benefitMatch = heading.match(/^(\d+(?:\.\d+)?% off)(.*)$/i);
          if (benefitMatch) {
            const benefit = document.createElement('span');
            benefit.className = 'dd-pdp-qualification__benefit';
            benefit.textContent = benefitMatch[1];
            this.qualificationHeading.replaceChildren(benefit, document.createTextNode(benefitMatch[2]));
          }
        }
        if (this.statusOutput) this.statusOutput.textContent = `${heading}. ${this.qualificationMessage.textContent}`;
        if (newShipping) this.qualificationNotice.classList.add('is-shipping');
        else this.qualificationNotice.classList.remove('is-shipping');
        this.qualificationNotice.hidden = false;
        this.startQualificationPositioning();
        this.qualificationFrame = window.requestAnimationFrame(() => {
          this.qualificationNotice.classList.add('is-visible');
        });
        this.qualificationTimer = window.setTimeout(() => this.hideQualification(true), 6000);
      }

      startQualificationPositioning() {
        this.stopQualificationPositioning();
        const notice = this.qualificationNotice;
        notice.style.removeProperty('top');
        notice.style.removeProperty('bottom');
        this.qualificationDefaultBottom = parseFloat(getComputedStyle(notice).bottom) || 90;
        this.handleQualificationPosition = () => {
          if (this.qualificationPositionFrame) return;
          this.qualificationPositionFrame = window.requestAnimationFrame(() => {
            this.qualificationPositionFrame = null;
            this.positionQualification();
          });
        };
        window.addEventListener('scroll', this.handleQualificationPosition, { passive: true });
        window.addEventListener('resize', this.handleQualificationPosition, { passive: true });
        this.positionQualification();
      }

      stopQualificationPositioning() {
        window.removeEventListener('scroll', this.handleQualificationPosition);
        window.removeEventListener('resize', this.handleQualificationPosition);
        window.cancelAnimationFrame(this.qualificationPositionFrame);
        this.qualificationPositionFrame = null;
      }

      positionQualification() {
        const notice = this.qualificationNotice;
        if (!notice || notice.hidden) return;
        const viewportHeight = window.innerHeight;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        let height = notice.offsetHeight;
        const headerBottom = [...document.querySelectorAll('sticky-header')].reduce((bottom, header) => {
          const rect = header.getBoundingClientRect();
          return rect.top < viewportHeight && rect.bottom > 0 ? Math.max(bottom, rect.bottom) : bottom;
        }, 0);
        const minTop = headerBottom + 24;
        const maxBottom = viewportHeight - 16;
        const shippingRows = [...this.querySelectorAll('[data-dd-pdp-shipping]')]
          .map((row) => row.getBoundingClientRect())
          .filter((rect) => rect.width > 0 && rect.height > 0 && rect.top >= minTop && rect.top < maxBottom);
        const shipping = shippingRows[0];
        notice.classList.add('is-shipping-overlay');
        const primary = this.submitButton?.getBoundingClientRect();
        const sticky = this.stickySubmitButton?.getBoundingClientRect();
        const anchor = shipping || (primary?.bottom > minTop && primary?.top < maxBottom ? primary : sticky);
        if (anchor?.width > 0) {
          const noticeWidth = Math.min(350, anchor.width - 4, window.innerWidth - 32);
          notice.style.width = `${noticeWidth}px`;
          notice.style.left = `${Math.max(16, anchor.left + (anchor.width - noticeWidth - 4) / 2)}px`;
        }
        const overlayHeight = notice.offsetHeight;
        if (shipping && shipping.top + overlayHeight <= maxBottom) {
          notice.classList.remove('is-occluded');
          notice.style.top = `${shipping.top + (mobile ? 0 : overlayHeight / 2)}px`;
          notice.style.bottom = 'auto';
          return;
        }
        height = overlayHeight;
        const buttons = this.submitButtons.map((button) => button.getBoundingClientRect())
          .filter((rect) => rect.width > 0 && rect.height > 0 && rect.bottom > minTop && rect.top < maxBottom);
        const top = this.qualificationPositionTop(buttons, height, minTop, maxBottom,
          mobile ? viewportHeight - this.qualificationDefaultBottom - height : viewportHeight * 0.58 - height / 2);
        notice.classList.toggle('is-occluded', top === null);
        if (top === null) return;
        // Desktop's existing animation uses a half-height Y translation; mobile uses only X.
        const cssTop = top + (mobile ? 0 : height / 2);
        const value = `${cssTop}px`;
        if (notice.style.top !== value) notice.style.top = value;
        if (notice.style.bottom !== 'auto') notice.style.bottom = 'auto';
      }

      qualificationPositionTop(buttons, height, minTop, maxBottom, fallback) {
        const fits = (top) => top >= minTop && top + height <= maxBottom &&
          buttons.every((rect) => top + height <= rect.top - 16 || top >= rect.bottom + 16);
        // Prefer the main CTA; if it is off-screen, the visible sticky CTA becomes the anchor.
        const anchor = buttons[0];
        if (anchor) {
          // Keep the product form clear; use above only when the viewport has no room below.
          const below = anchor.bottom + 16;
          if (fits(below)) return below;
          const above = anchor.top - 16 - height;
          if (fits(above)) return above;
        }
        const clamped = Math.max(minTop, Math.min(fallback, maxBottom - height));
        if (fits(clamped)) return clamped;
        const alternatives = [minTop, ...buttons.flatMap((rect) => [rect.top - 16 - height, rect.bottom + 16])];
        return alternatives.find(fits) ?? null;
      }

      hideQualification(animate = false) {
        window.clearTimeout(this.qualificationTimer);
        window.clearTimeout(this.qualificationHideTimer);
        window.cancelAnimationFrame(this.qualificationFrame);
        this.stopQualificationPositioning();
        if (!this.qualificationNotice) return;
        this.qualificationNotice.classList.remove('is-visible');
        if (animate) {
          this.qualificationHideTimer = window.setTimeout(() => {
            this.qualificationNotice.hidden = true;
          }, 260);
        } else {
          this.qualificationNotice.hidden = true;
        }
      }

      async refreshCommerceCart(event) {
        const requestId = (this.commerceRequestId || 0) + 1;
        this.commerceRequestId = requestId;
        try {
          let cart = event?.cartData;
          if (!Array.isArray(cart?.items) || !Number.isFinite(cart.total_price)) {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
            if (!response.ok) return;
            cart = await response.json();
          }
          if (!this.isConnected || requestId !== this.commerceRequestId) return;
          this.commerceCart = cart;
          this.hideQualification();
          this.updateSummary();
        } catch (_error) {
          this.commerceCart = {};
          this.updateSummary();
        }
      }

      getLines() {
        const currentRows = this.rows.filter((row) => row.dataset.rowType === 'current');
        const additionalRows = this.rows.filter((row) => row.dataset.rowType !== 'current');

        return [...currentRows, ...additionalRows].map((row) => ({
          rowType: row.dataset.rowType,
          productId: row.dataset.productId,
          selectionUnit: row.dataset.selectionUnit,
          selectionUnitLabel: row.dataset.selectionUnitLabel,
          selectionUnitPlural: row.dataset.selectionUnitPlural,
          variantId: row.dataset.variantId,
          quantity: this.readNumber(row.querySelector('[data-dd-pdp-quantity]')?.dataset.quantity),
          unitPrice: this.readNumber(row.dataset.unitPrice),
          available: row.dataset.available === 'true',
          mixMatchEligible: row.dataset.mixMatchEligible === 'true',
        }));
      }

      async handleSubmitRequest(event) {
        event.preventDefault();
        if (this.isSubmitting || this.isCartPending || this.isAdded) return;

        const lines = this.getLines().filter(
          (line) => line.available && line.variantId && line.quantity > 0
        );
        const currentLine = lines.find((line) => line.rowType === 'current');
        if (!currentLine) return;

        const submitTrigger = this.submitTrigger || event.submitter || this.submitButton;
        this.isCartPending = true;
        this.hideQualification();
        let addState = 'not-started';
        let navigatingToCart = false;
        this.clearMessages();
        this.setAdded(false);
        this.setLoading(true);
        const cartLinesUpdate = this.createCartLinesUpdateEvent(lines);

        this.dispatchEvent(
          new CustomEvent('dd:pdp-add-requested', {
            bubbles: true,
            detail: { lines },
          })
        );

        try {
          const cart = document.querySelector('cart-drawer');
          const sectionIds = cart
            ? cart.getSectionsToRender().map((section) => section.id)
            : ['cart-icon-bubble'];
          const request = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: lines.map((line) => ({
                id: Number(line.variantId),
                quantity: line.quantity,
                ...(line.rowType === 'current' ? this.getCurrentLineProperties() : (window.DDTacoOffer ? { properties: { _dd_stash: 'true' } } : {})),
              })),
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          };
          addState = 'pending';
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', request);
          if ([404, 422].includes(response.status)) addState = 'rejected';
          const responseData = await response.json();

          if (!response.ok || responseData.status) {
            if ([404, 422].includes(Number(responseData.status || response.status))) addState = 'rejected';
            const readableMessage = [responseData.description, responseData.message].find(
              (value) => typeof value === 'string' && value.trim()
            );
            const error = new Error(readableMessage || this.dataset.ddCartError);
            error.errors = responseData.errors;
            throw error;
          }

          addState = 'accepted';
          this.resolveCartLinesUpdate(cartLinesUpdate);
          this.recipientForm?.resetRecipientForm?.();
          this.announceSuccess();
          this.setLoading(false);
          this.setAdded(true);
          this.dispatchEvent(
            new CustomEvent('dd:pdp-added', {
              bubbles: true,
              detail: { lines, cartData: responseData },
            })
          );

          const committedCartData = await this.renderCartState(cart, responseData, submitTrigger);

          try {
            await cart.publishCartUpdate?.(committedCartData, 'dd-pdp', {
              productVariantId: currentLine.variantId,
            });
          } catch (error) {
            console.warn('Desert Dust cart update notification failed.', error);
          }
        } catch (error) {
          if (addState === 'accepted' || addState === 'pending') {
            if (addState === 'pending') cartLinesUpdate?.reject(error);
            navigatingToCart = true;
            this.handleRecoveryPageShow = () => {
              this.isCartPending = false;
              this.setAdded(false);
              this.setLoading(false);
            };
            window.addEventListener('pageshow', this.handleRecoveryPageShow, { once: true });
            window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
            return;
          }
          this.setAdded(false);
          cartLinesUpdate?.reject(error);
          const message = error.message || this.dataset.ddCartError;
          this.showError(message);
          this.recipientForm?.displayErrorMessage?.(message, error.errors);
          this.dispatchCartErrorEvent(message);
        } finally {
          if (!navigatingToCart) this.isCartPending = false;
          this.setLoading(false);
        }
      }

      getCurrentLineProperties() {
        if (this.dataset.productFormMode !== 'single_product' || !this.form) return {};

        const properties = {};
        new FormData(this.form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)]$/);
          if (!match || value === '') return;
          properties[match[1]] = value;
        });
        return Object.keys(properties).length ? { properties } : {};
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;
        this.toggleAttribute('aria-busy', isLoading);
        this.submitButtons.forEach((button) => {
          button.classList.toggle('is-loading', isLoading);
          button.setAttribute('aria-busy', String(isLoading));
        });
        this.availableAtcContents.forEach((content) => {
          content.hidden = isLoading || this.isAdded || this.dataset.currentProductAvailable !== 'true';
        });
        this.soldOutAtcContents.forEach((content) => {
          content.hidden = isLoading || this.isAdded || this.dataset.currentProductAvailable === 'true';
        });
        this.loadingAtcContents.forEach((content) => {
          content.hidden = !isLoading;
        });
        this.addedAtcContents.forEach((content) => {
          content.hidden = isLoading || !this.isAdded;
        });

        this.querySelectorAll?.(
          '[data-dd-pdp-quantity-minus], [data-dd-pdp-quantity-plus], [data-dd-pdp-option-value]'
        )?.forEach((control) => {
          control.disabled = isLoading || this.isCartPending;
        });

        if (!isLoading && !this.isCartPending) {
          this.rows.forEach((row) => {
            const selector = row.querySelector('[data-dd-pdp-quantity]');
            if (!selector) return;
            const maximum = selector.dataset.max === undefined ? null : this.readNumber(selector.dataset.max);
            this.setQuantity(selector, this.readNumber(selector.dataset.quantity), maximum);
          });
          this.updateVariantOptionAvailability();
        }
        this.updateSummary();
      }

      setAdded(isAdded) {
        this.isAdded = isAdded;
        window.clearTimeout(this.addedStateTimer);
        this.submitButtons.forEach((button) => button.classList.toggle('is-added', isAdded));
        this.availableAtcContents.forEach((content) => {
          content.hidden = isAdded || this.isSubmitting || this.dataset.currentProductAvailable !== 'true';
        });
        this.soldOutAtcContents.forEach((content) => {
          content.hidden = isAdded || this.isSubmitting || this.dataset.currentProductAvailable === 'true';
        });
        this.loadingAtcContents.forEach((content) => {
          content.hidden = !this.isSubmitting;
        });
        this.addedAtcContents.forEach((content) => {
          content.hidden = !isAdded || this.isSubmitting;
        });

        if (isAdded) {
          this.addedStateTimer = window.setTimeout(() => this.setAdded(false), 1600);
        }
        this.updateSummary();
      }

      setupStickyAtc() {
        if (!this.primaryAtc || !this.stickyAtc || !('IntersectionObserver' in window)) return;

        this.mobileViewport = window.matchMedia('(max-width: 749px)');
        this.desktopStickyTrigger = document.querySelector('.dd-asset-claims--product-stamps') || this.primaryAtc;
        this.stickyTriggerHasPassed = false;
        this.stickyAtcMenuOpen = false;
        this.stickyAtcMenuScrollY = undefined;
        this.mobileMenuClosePending = false;
        this.mobileMenuReopenRequested = false;
        this.mobileMenuDetails = document.getElementById('Details-menu-drawer-container');
        this.mobileMenuSummary = this.mobileMenuDetails?.querySelector(':scope > summary');
        this.mobileMenuDrawer = this.mobileMenuDetails?.closest('header-drawer');
        this.handleViewportChange = () => this.syncStickyAtcPosition();
        this.handleMobileMenuToggle = () => {
          const isOpen = Boolean(this.mobileMenuDetails?.open);

          if (isOpen) {
            this.mobileMenuClosePending = false;
            this.captureMobileMenuScroll();
            this.stickyAtcMenuOpen = true;
            this.updateStickyAtcVisibility();
            return;
          }

          this.mobileMenuDetails.classList.remove('menu-opening');

          if (this.mobileMenuReopenRequested) {
            this.mobileMenuClosePending = false;
            this.mobileMenuReopenRequested = false;
            this.mobileMenuDetails.open = true;
            this.mobileMenuDrawer?.openMenuDrawer?.(this.mobileMenuSummary);
            this.stickyAtcMenuOpen = true;
            this.updateStickyAtcVisibility();
            return;
          }

          const scrollY = this.stickyAtcMenuScrollY;
          this.mobileMenuClosePending = false;
          this.stickyAtcMenuScrollY = undefined;
          this.stickyAtcMenuOpen = false;
          this.restoreMobileMenuScroll(scrollY);
        };
        this.handleMobileMenuPress = (event) => {
          if (this.isMobileMenuClosing()) {
            event.preventDefault();
            this.mobileMenuReopenRequested = true;
            return;
          }
          if (!this.mobileMenuDetails?.open) this.captureMobileMenuScroll();
          this.stickyAtcMenuOpen = true;
          this.updateStickyAtcVisibility();
        };
        this.handleMobileMenuClick = (event) => {
          if (this.mobileMenuReopenRequested || this.isMobileMenuClosing()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.mobileMenuReopenRequested = true;
            return;
          }
          if (!this.mobileMenuDetails?.open) {
            this.captureMobileMenuScroll();
            this.stickyAtcMenuOpen = true;
            this.updateStickyAtcVisibility();
            return;
          }
          this.mobileMenuClosePending = true;
        };
        this.handleMobileMenuKeyUp = (event) => {
          if (event.code?.toUpperCase() !== 'ESCAPE' || !this.mobileMenuDetails?.open) return;
          this.mobileMenuClosePending = true;
        };
        this.handleMobileMenuBlockSelect = (event) => {
          if (!this.mobileViewport.matches || this.mobileMenuDetails?.open) return;
          const blockId = event.detail?.blockId;
          if (!blockId) return;

          const mobileMenu = this.mobileMenuDetails?.querySelector('dd-mobile-menu');
          const selectedBlock = Array.from(mobileMenu?.querySelectorAll('[data-dd-block-id]') || []).find(
            (item) => item.dataset.ddBlockId === blockId
          );
          if (!selectedBlock) return;

          this.captureMobileMenuScroll();
          this.stickyAtcMenuOpen = true;
          this.updateStickyAtcVisibility();
        };
        this.handleStickyPositionChange = () => {
          if (this.stickyPositionFrame) return;
          this.stickyPositionFrame = window.requestAnimationFrame(() => {
            this.stickyPositionFrame = undefined;
            this.syncStickyAtcPosition();
          });
        };
        this.mobileViewport.addEventListener?.('change', this.handleViewportChange);
        window.addEventListener('scroll', this.handleStickyPositionChange, { passive: true });
        this.mobileMenuDetails?.addEventListener('toggle', this.handleMobileMenuToggle);
        this.mobileMenuDetails?.addEventListener('keyup', this.handleMobileMenuKeyUp, true);
        this.mobileMenuSummary?.addEventListener('pointerdown', this.handleMobileMenuPress, true);
        this.mobileMenuSummary?.addEventListener('click', this.handleMobileMenuClick, true);
        document.addEventListener('shopify:block:select', this.handleMobileMenuBlockSelect, true);

        this.stickyAtcObserver = new IntersectionObserver(
          () => this.syncStickyAtcPosition(),
          { threshold: 0 }
        );
        this.stickyAtcObserver.observe(this.primaryAtc);
        if (this.desktopStickyTrigger !== this.primaryAtc) this.stickyAtcObserver.observe(this.desktopStickyTrigger);
        this.syncStickyAtcPosition();
      }

      isMobileMenuClosing() {
        return Boolean(
          this.mobileMenuClosePending &&
            this.mobileMenuDetails?.open &&
            !this.mobileMenuDetails.classList.contains('menu-opening')
        );
      }

      captureMobileMenuScroll() {
        const activeRestoreTarget =
          Number.isFinite(this.mobileMenuRestoreTargetY) &&
          performance.now() <= this.mobileMenuRestoreTargetExpiresAt + 1000
            ? this.mobileMenuRestoreTargetY
            : undefined;
        this.cancelMobileMenuScrollRestore?.();
        this.cancelMobileMenuRestoreGrace?.();
        if (!Number.isFinite(this.stickyAtcMenuScrollY)) {
          this.stickyAtcMenuScrollY = Number.isFinite(activeRestoreTarget)
            ? activeRestoreTarget
            : window.scrollY;
        }
        this.mobileMenuRestoreTargetY = undefined;
        this.mobileMenuRestoreTargetExpiresAt = undefined;

        if (window.scrollY > 0) {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
      }

      restoreMobileMenuScroll(scrollY) {
        window.cancelAnimationFrame(this.mobileMenuRestoreFrame);
        this.cancelMobileMenuScrollRestore?.();
        this.cancelMobileMenuRestoreGrace?.();

        if (!Number.isFinite(scrollY)) {
          this.syncStickyAtcPosition();
          return;
        }

        const shouldRestoreFocus = document.activeElement === this.mobileMenuSummary;
        const settleUntil = performance.now() + 750;
        this.mobileMenuRestoreTargetY = scrollY;
        this.mobileMenuRestoreTargetExpiresAt = settleUntil;
        const restorePosition = () => {
          if (Math.abs(window.scrollY - scrollY) > 1) {
            window.scrollTo({ top: scrollY, behavior: 'auto' });
          }
          this.syncStickyAtcPosition();
        };
        const cancelOnUserInput = (event) => {
          const isMenuTarget = Boolean(
            event.composedPath?.().includes(this.mobileMenuSummary) || event.target === this.mobileMenuSummary
          );
          const isMenuActivation = Boolean(
            isMenuTarget &&
              (event.type === 'pointerdown' ||
                event.type === 'touchstart' ||
                (event.type === 'keydown' && ['Enter', ' '].includes(event.key)))
          );
          if (!isMenuActivation) {
            this.mobileMenuRestoreTargetY = undefined;
            this.mobileMenuRestoreTargetExpiresAt = undefined;
          }
          this.cancelMobileMenuScrollRestore?.();
        };
        const cancelEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

        this.mobileMenuScrollRestoreCancelled = false;
        this.cancelMobileMenuScrollRestore = () => {
          if (this.mobileMenuScrollRestoreCancelled) return;
          this.mobileMenuScrollRestoreCancelled = true;
          window.cancelAnimationFrame(this.mobileMenuRestoreFrame);
          cancelEvents.forEach((eventName) => {
            window.removeEventListener(eventName, cancelOnUserInput, true);
          });
        };
        cancelEvents.forEach((eventName) => {
          window.addEventListener(eventName, cancelOnUserInput, { capture: true, passive: true });
        });

        if (shouldRestoreFocus) this.mobileMenuSummary.focus({ preventScroll: true });

        const settle = (now) => {
          if (this.mobileMenuScrollRestoreCancelled || this.mobileMenuDetails?.open) {
            this.cancelMobileMenuScrollRestore();
            return;
          }

          restorePosition();
          if (now < settleUntil) {
            this.mobileMenuRestoreFrame = window.requestAnimationFrame(settle);
            return;
          }

          if (shouldRestoreFocus) this.mobileMenuSummary.focus({ preventScroll: true });
          restorePosition();
          this.cancelMobileMenuScrollRestore();
          this.armMobileMenuRestoreGrace();
        };

        this.mobileMenuRestoreFrame = window.requestAnimationFrame(settle);
      }

      armMobileMenuRestoreGrace() {
        if (!Number.isFinite(this.mobileMenuRestoreTargetY)) return;

        const graceEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
        const clearGrace = () => {
          window.clearTimeout(this.mobileMenuRestoreGraceTimer);
          graceEvents.forEach((eventName) => {
            window.removeEventListener(eventName, this.mobileMenuRestoreGraceHandler, true);
          });
          this.mobileMenuRestoreGraceHandler = undefined;
          this.mobileMenuRestoreGraceTimer = undefined;
          this.cancelMobileMenuRestoreGrace = undefined;
          this.mobileMenuRestoreTargetY = undefined;
          this.mobileMenuRestoreTargetExpiresAt = undefined;
        };

        this.mobileMenuRestoreGraceHandler = (event) => {
          const isMenuTarget = Boolean(
            event.composedPath?.().includes(this.mobileMenuSummary) || event.target === this.mobileMenuSummary
          );
          const isMenuActivation = Boolean(
            isMenuTarget &&
              (event.type === 'pointerdown' ||
                event.type === 'touchstart' ||
                (event.type === 'keydown' && ['Enter', ' '].includes(event.key)))
          );
          if (!isMenuActivation) clearGrace();
        };
        this.cancelMobileMenuRestoreGrace = clearGrace;
        graceEvents.forEach((eventName) => {
          window.addEventListener(eventName, this.mobileMenuRestoreGraceHandler, { capture: true, passive: true });
        });
        this.mobileMenuRestoreGraceTimer = window.setTimeout(clearGrace, 1000);
      }

      syncStickyAtcPosition() {
        const trigger = this.mobileViewport.matches ? this.primaryAtc : this.desktopStickyTrigger;
        this.stickyTriggerHasPassed = trigger.getBoundingClientRect().bottom <= 0;
        this.updateStickyAtcVisibility();
      }

      updateStickyAtcVisibility() {
        if (!this.stickyAtc || !this.stickySubmitButton) return;
        const shouldShow = Boolean(
          this.stickyTriggerHasPassed && !this.stickyAtcMenuOpen
        );
        this.stickyAtc.hidden = !shouldShow;
        this.stickyAtc.setAttribute('aria-hidden', String(!shouldShow));
        this.stickySubmitButton.tabIndex = shouldShow ? 0 : -1;
      }

      clearMessages() {
        if (this.errorOutput) {
          this.errorOutput.textContent = '';
          this.errorOutput.hidden = true;
        }
        if (this.statusOutput) this.statusOutput.textContent = '';
      }

      showError(message) {
        if (!this.errorOutput) return;
        this.errorOutput.textContent = message;
        this.errorOutput.hidden = false;
      }

      announceSuccess() {
        if (this.statusOutput) this.statusOutput.textContent = this.dataset.ddCartSuccess;
      }

      async renderCartState(cart, responseData, submitTrigger = this.submitButton) {
        if (!cart?.commitContents) throw new Error(this.dataset.ddCartError);

        // A cart opened during the add delay keeps its own invoking control.
        if (!cart.classList.contains('active') && !cart.isOpening) cart.setActiveElement?.(submitTrigger);
        if (window.DDTacoOffer) {
          const state = await cart.commitContents(responseData, { shouldOpen: false });
          const lines = this.getLines().filter((line) => line.available && line.variantId && line.quantity > 0);
          const shown = await window.DDTacoOffer.maybeShow({ cart, lines });
          if (!shown) cart.open(submitTrigger);
          return state;
        }
        return cart.commitContents(responseData, { shouldOpen: true });
      }

      createCartLinesUpdateEvent(lines) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: lines.map((line) => ({ merchandiseId: line.variantId, quantity: line.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        fetch(`${window.routes?.cart_url || '/cart'}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart?.currency) throw new Error(this.dataset.ddCartError);
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      }

      readVariants() {
        const source = this.querySelector('[data-dd-pdp-variants]');
        if (!source) return [];

        try {
          return JSON.parse(source.textContent || '[]');
        } catch (error) {
          console.warn('Desert Dust PDP could not read variant data.', error);
          return [];
        }
      }

      getSelectedOptions() {
        return Array.from(this.querySelectorAll('[data-dd-pdp-option]')).map(
          (group) => group.querySelector('[data-dd-pdp-option-value]:checked')?.value
        );
      }

      findSelectedVariant() {
        const selectedOptions = this.getSelectedOptions();
        if (!selectedOptions.length || selectedOptions.some((value) => value === undefined)) return null;

        return (
          this.variants.find((variant) =>
            variant.options?.every((value, index) => value === selectedOptions[index])
          ) || null
        );
      }

      hydrateCurrentVariantInput() {
        if (!this.variantInput) return;

        const currentRow = this.rows.find((row) => row.dataset.rowType === 'current');
        const selectedVariant = this.findSelectedVariant();
        const variantId = selectedVariant?.id || currentRow?.dataset.variantId || '';
        this.variantInput.value = String(variantId);
        this.variantInput.setAttribute('value', String(variantId));
      }

      updateVariantOptionAvailability() {
        if (!this.variantPicker || !this.variants.length) return;

        const selectedOptions = this.getSelectedOptions();
        this.querySelectorAll('[data-dd-pdp-option-value]').forEach((input) => {
          const position = Math.max(0, this.readNumber(input.dataset.optionPosition, 1) - 1);
          const candidateOptions = [...selectedOptions];
          candidateOptions[position] = input.value;

          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options?.every(
                (value, index) => candidateOptions[index] === undefined || value === candidateOptions[index]
              )
          );

          input.disabled = !available;
          const unavailable = input.nextElementSibling?.querySelector('[data-dd-pdp-option-unavailable]');
          if (unavailable) unavailable.hidden = available;
        });
      }

      applyVariant(variant) {
        const currentRow = this.rows.find((row) => row.dataset.rowType === 'current');
        const selector = currentRow?.querySelector('[data-dd-pdp-quantity]');
        if (!currentRow || !selector) return;

        const available = Boolean(variant?.available);
        const quantityRule = variant?.quantity_rule || {};
        const minimum = Math.max(1, this.readNumber(quantityRule.min, 1));
        const step = Math.max(1, this.readNumber(quantityRule.increment, 1));
        const maximum = quantityRule.max == null ? null : this.readNumber(quantityRule.max);

        currentRow.dataset.variantId = variant?.id ? String(variant.id) : '';
        currentRow.dataset.unitPrice = String(variant?.price || 0);
        currentRow.dataset.available = String(available);
        currentRow.classList.toggle('dd-pdp-purchase-row--unavailable', !available);
        this.dataset.currentProductAvailable = String(available);

        this.variantInput = this.querySelector('[data-dd-pdp-current-variant-id]');
        if (this.variantInput) {
          const variantId = variant?.id ? String(variant.id) : '';
          this.variantInput.value = variantId;
          this.variantInput.setAttribute('value', variantId);
        }

        selector.dataset.min = String(minimum);
        selector.dataset.step = String(step);
        if (maximum === null) {
          delete selector.dataset.max;
        } else {
          selector.dataset.max = String(maximum);
        }

        const currentQuantity = this.readNumber(selector.dataset.quantity);
        const nextQuantity = available
          ? Math.min(Math.max(currentQuantity, minimum), maximum === null ? Infinity : maximum)
          : 0;
        this.setQuantity(selector, nextQuantity, maximum);

        if (this.currentPriceOutput) {
          this.currentPriceOutput.textContent = this.formatMoney(variant?.price || 0);
        }

        const hasComparePrice = Boolean(
          variant?.compare_at_price && variant.compare_at_price > (variant?.price || 0)
        );
        if (this.comparePriceOutput) {
          this.comparePriceOutput.textContent = this.formatMoney(variant?.compare_at_price || 0);
          this.comparePriceOutput.hidden = !hasComparePrice;
        }
        if (this.regularPriceLabel) this.regularPriceLabel.hidden = hasComparePrice;
        if (this.salePriceLabel) this.salePriceLabel.hidden = !hasComparePrice;

        this.availableAtcContents.forEach((content) => {
          content.hidden = !available;
        });
        this.soldOutAtcContents.forEach((content) => {
          content.hidden = available;
        });

        this.updateSummary(true);

        if (variant?.id) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url);
        }

        this.dispatchEvent(
          new CustomEvent('dd:pdp-variant-change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{ amount }}';
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return this.formatWithDelimiters(cents, 2, ',', '.');

        const formats = {
          amount: [2, ',', '.'],
          amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','],
          amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_apostrophe_separator: [2, "'", '.'],
          amount_with_space_separator: [2, ' ', ','],
          amount_no_decimals_with_space_separator: [0, ' ', ','],
        };
        const values = formats[match[1]] || formats.amount;
        return format.replace(match[0], this.formatWithDelimiters(cents, ...values));
      }

      formatWithDelimiters(cents, precision, thousands, decimal) {
        const value = (this.readNumber(cents) / 100).toFixed(precision);
        const parts = value.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length === 2 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      }

      readNumber(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      }
    }
  );
}
